import { useCallback, useEffect, useMemo, useState } from 'react';
import { Table, Card, Input, Button, Space, Popconfirm, Tooltip, message } from 'antd';
import { SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType, ColumnType } from 'antd/es/table';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import { listProjectSpaceProjects, getProject, deleteProject } from '@/api/config';
import type { MainlineProject, Project } from '@/types';
import ProjectFormModal from '@/components/ProjectFormModal';
import { useConfigStore } from '@/store/configStore';

/** Build distinct filter options from a data array for a given field. */
function makeFilters(data: MainlineProject[], getValue: (row: MainlineProject) => string) {
  const seen = new Set<string>();
  const result: { text: string; value: string }[] = [];
  for (const row of data) {
    const v = getValue(row) || '-';
    if (!seen.has(v)) {
      seen.add(v);
      result.push({ text: v, value: v });
    }
  }
  return result.sort((a, b) => a.text.localeCompare(b.text));
}

/** Text search filter dropdown (for project name). */
function textSearchColumn(_dataIndex: keyof MainlineProject | 'mountain'): Partial<ColumnType<MainlineProject>> {
  let searchInput: HTMLInputElement | null = null;
  return {
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: FilterDropdownProps) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={(node) => { searchInput = node?.input ?? null; }}
          placeholder="搜索..."
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => confirm()}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button type="primary" onClick={() => confirm()} icon={<SearchOutlined />} size="small">
            搜索
          </Button>
          <Button onClick={() => { clearFilters?.(); confirm(); }} size="small">
            重置
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />,
    filterDropdownProps: {
      onOpenChange: (open: boolean) => { if (open) setTimeout(() => searchInput?.focus(), 100); },
    },
  };
}

const ProjectSpaceList = () => {
  const navigate = useNavigate();
  const { fetchProjects } = useConfigStore();
  const [data, setData] = useState<MainlineProject[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);

  const reload = () => {
    setLoading(true);
    listProjectSpaceProjects()
      .then((res) => {
        if (res.code === 200) setData(res.data);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
  }, []);

  const handleCreate = () => {
    setEditingProject(null);
    setModalOpen(true);
  };

  const handleEdit = useCallback(async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const project = await getProject(String(id));
    setEditingProject(project);
    setModalOpen(true);
  }, []);

  const handleDelete = useCallback(async (id: number) => {
    await deleteProject(String(id));
    message.success('删除成功');
    window.dispatchEvent(new CustomEvent('mainline-project-changed'));
    reload();
    fetchProjects();
  }, [fetchProjects]);

  const columns = useMemo((): ColumnsType<MainlineProject> => [
    {
      title: '项目名称',
      dataIndex: 'project',
      key: 'project',
      sorter: (a, b) => a.project.localeCompare(b.project),
      onFilter: (value, record) => record.project.toLowerCase().includes(String(value).toLowerCase()),
      ...textSearchColumn('project'),
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      render: (v: string) => v ? new Date(v).toLocaleDateString('zh-CN') : '-',
      sorter: (a, b) => {
        const ta = a.createTime ? new Date(a.createTime).getTime() : 0;
        const tb = b.createTime ? new Date(b.createTime).getTime() : 0;
        return ta - tb;
      },
    },
    {
      title: 'owner',
      dataIndex: 'owner',
      key: 'owner',
      render: (v: string | null) => v || '-',
      sorter: (a, b) => (a.owner || '').localeCompare(b.owner || ''),
      filters: makeFilters(data, (r) => r.owner || ''),
      onFilter: (value, record) => (record.owner || '') === value,
      filterSearch: true,
    },
    {
      title: '基础项目',
      key: 'baseProject',
      render: (_, row) => row.shared_from_project_name || '-',
      filters: makeFilters(data, (r) => r.shared_from_project_name || ''),
      onFilter: (value, record) => (record.shared_from_project_name || '') === value,
      filterSearch: true,
    },
    {
      title: 'OS',
      dataIndex: 'os',
      key: 'os',
      sorter: (a, b) => (a.os || '').localeCompare(b.os || ''),
      filters: makeFilters(data, (r) => r.os),
      onFilter: (value, record) => (record.os || '') === value,
      filterSearch: true,
    },
    {
      title: '平台',
      dataIndex: 'platform',
      key: 'platform',
      sorter: (a, b) => (a.platform || '').localeCompare(b.platform || ''),
      filters: makeFilters(data, (r) => r.platform),
      onFilter: (value, record) => (record.platform || '') === value,
      filterSearch: true,
    },
    {
      title: '域控形态',
      dataIndex: 'adcuModel',
      key: 'adcuModel',
      sorter: (a, b) => (a.adcuModel || '').localeCompare(b.adcuModel || ''),
      filters: makeFilters(data, (r) => r.adcuModel),
      onFilter: (value, record) => (record.adcuModel || '') === value,
      filterSearch: true,
    },
    {
      title: 'MCU型号',
      dataIndex: 'mcuModel',
      key: 'mcuModel',
      render: (v) => v ?? '-',
      sorter: (a, b) => (a.mcuModel || '').localeCompare(b.mcuModel || ''),
      filters: makeFilters(data, (r) => r.mcuModel || ''),
      onFilter: (value, record) => (record.mcuModel || '') === value,
      filterSearch: true,
    },
    {
      title: '操作',
      key: 'actions',
      width: 100,
      render: (_, row) => (
        <Space size={4} onClick={(e) => e.stopPropagation()}>
          <Button size="small" icon={<EditOutlined />} onClick={(e) => handleEdit(row.id, e)} />
          <Tooltip title={!row.userCanEdit ? '无需求编辑权限' : undefined}>
            <Popconfirm
              title="确认删除该项目？"
              onConfirm={() => handleDelete(row.id)}
              disabled={!row.userCanEdit}
            >
              <Button size="small" danger icon={<DeleteOutlined />} disabled={!row.userCanEdit} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ], [data, handleEdit, handleDelete]);

  return (
    <Card
      title="项目空间"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          创建项目
        </Button>
      }
    >
      <Table
        rowKey="id"
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (total) => `共 ${total} 条` }}
        onRow={(row) => ({
          onClick: () => navigate(row.hasRequirements ? `/requirements/${row.id}` : `/packages/${row.id}`),
          style: { cursor: 'pointer' },
        })}
      />
      <ProjectFormModal
        open={modalOpen}
        project={editingProject}
        defaultMainline={false}
        onSuccess={() => {
          setModalOpen(false);
          reload();
          fetchProjects(true);
        }}
        onCancel={() => setModalOpen(false)}
      />
    </Card>
  );
};

export default ProjectSpaceList;
